import { useFormatting } from './Formatting.js';
import { Tooltip } from './Tooltip.js';
import './Numeric.css';

export interface TimestampProps {
  /** ISO 8601 or any value the Date constructor accepts. Always stored/passed as UTC. */
  value: string | Date;
  /**
   * IANA timezone, e.g. "Europe/Berlin".
   *
   * ⚠️ **Omit it.** M30's `FormattingProvider` supplies the organisation's, which is what
   * [BR-10](../../../docs/platform/06-prd.md) requires — and until 2026-08-06 this prop's only
   * fallback was the *browser's*, so two colleagues saw different times for the same audit event and
   * neither was told why. Pass one only to override deliberately (a gallery, a fixture).
   */
  timeZone?: string;
  locale?: string;
}

/**
 * Renders in the tenant's timezone with the UTC equivalent available on
 * **hover and keyboard focus** — closes audit gap C2 (the prototype only
 * exposed UTC on hover, which fails a keyboard-only user by construction;
 * see docs/design-system/a11y.md §5 and content.md).
 */
export function Timestamp({ value, timeZone, locale }: TimestampProps) {
  const formatting = useFormatting();
  const date = typeof value === 'string' ? new Date(value) : value;
  /* An explicit prop wins; otherwise the organisation's, and only then the runtime's — which the
     provider has already resolved and flagged as `inferred`. */
  const tz = timeZone ?? formatting.timeZone;
  const resolvedLocale = locale ?? formatting.locale;
  const formatted = new Intl.DateTimeFormat(resolvedLocale, {
    timeZone: tz,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
  const utcFormatted = new Intl.DateTimeFormat(resolvedLocale, {
    timeZone: 'UTC',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);

  return (
    <Tooltip content={`${utcFormatted} UTC`}>
      {/* Tabular figures: a column of timestamps in a run/export list must
          align, and the prototype sets tabular-nums on every one (audit F-9). */}
      <time className="dpr-numeric" dateTime={date.toISOString()} tabIndex={0}>
        {formatted}
      </time>
    </Tooltip>
  );
}
